import { DateFormElementAnswerSchema, DateFormElement as DateFormElementType, FormElementKind } from '@gearbelt/global';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { NativeDatePicker } from '@/components/ui/native-date-picker';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { DateConfigLabels } from '../config-field-labels';
import { ConfigLabel } from '../config-label';
import { FieldShell } from '../field-shell';
import { LockedFieldTooltip } from './locked-field-tooltip';

export const DateFormElement = ({
  element,
  value,
  onChange,
  onBlur,
  readonly,
  lockedNotLatest,
  hideLabel,
  error,
}: {
  element: DateFormElementType;
  value?: z.infer<typeof DateFormElementAnswerSchema>['value'];
  onChange: (value: z.infer<typeof DateFormElementAnswerSchema>['value']) => void;
  onBlur?: () => void;
  readonly?: boolean;
  lockedNotLatest?: boolean;
  hideLabel?: boolean;
  error?: string;
}) => {
  // Convert ISO string to Date object
  const dateValue = value ? new Date(value) : undefined;

  const handleDateChange = (newDate: Date | null) => {
    if (!newDate) {
      onChange(null);
      return;
    }

    // Convert to ISO string for form submission
    onChange(newDate.toISOString());
  };

  // Determine which type of picker to show based on configuration
  const getDatePickerType = () => {
    if ((element.config?.showDate ?? true) && element.config?.showTime) {
      return 'datetime-local';
    } else if (!(element.config?.showDate ?? true) && element.config?.showTime) {
      return 'time';
    } else {
      return 'date';
    }
  };

  return (
    <FieldShell
      label={element.label}
      required={element.required}
      hideLabel={hideLabel}
      labelAddon={
        lockedNotLatest ? (
          <LockedFieldTooltip
            ariaLabel="Why can't I edit this completion date?"
            message="The completion date can only be edited on the latest schedule check record."
          />
        ) : null
      }
      error={error}>
      <NativeDatePicker
        className="cursor-pointer"
        type={getDatePickerType()}
        value={dateValue || null}
        onChange={handleDateChange}
        onBlur={onBlur}
        placeholder={`Select ${element.label}`}
        disabled={readonly}
        error={!!error}
      />
    </FieldShell>
  );
};

export function DateFormElementConfig({ element, onChange }: { element: DateFormElementType; onChange?: (updates: Partial<DateFormElementType>) => void }) {
  const form = useFormContext<{ elements: Array<{ id: string; kind: FormElementKind; config?: { isSubmissionDate?: boolean } }> }>();
  const formElements = form.watch('elements');

  const isSubmissionDateTaken = formElements.some((el) => el.kind === FormElementKind.Date && el.id !== element.id && el.config?.isSubmissionDate);

  const isSubmissionDateDisabled = isSubmissionDateTaken && !element.config?.isSubmissionDate;

  return (
    <div className="flex flex-col gap-4">
      <ConfigLabel>
        <span>Label:</span>
        <Input value={element.label} onChange={(e) => onChange?.({ label: e.target.value })} />
      </ConfigLabel>

      <ConfigLabel className="flex-row items-start gap-2">
        <Checkbox checked={element.required} onCheckedChange={(checked) => onChange?.({ required: !!checked })} />
        <span>Required</span>
      </ConfigLabel>

      {element.config?.showTime && (
        <ConfigLabel className="flex-row items-start gap-2">
          {isSubmissionDateDisabled ? (
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <span>
                    <Checkbox checked={false} disabled />
                  </span>
                </TooltipTrigger>
                <TooltipPortal>
                  <TooltipContent side="top" className="bg-default-text text-white">
                    Only one date field can be used as the submission date per form
                    <TooltipArrow className="fill-default-text" />
                  </TooltipContent>
                </TooltipPortal>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Checkbox
              checked={element.config?.isSubmissionDate ?? false}
              onCheckedChange={(checked) => onChange?.({ config: { ...element.config, isSubmissionDate: !!checked } } as Partial<DateFormElementType>)}
            />
          )}
          <span>{DateConfigLabels.isSubmissionDate}</span>
        </ConfigLabel>
      )}
    </div>
  );
}
